import { useEffect, useMemo, useState } from 'react';
import {
  listTasks, listProjects, listTaskUsers, searchTasks, createProject, updateTask,
  listNotifications, countNotifications, markAllNotificationsSeen, markNotificationSeen,
  type TaskListItem, type TaskStatus, type TaskPriority, type TaskProject, type TaskUser, type TaskNotification,
} from '../api/tasks';
import { listSpaces, getMe, type SpaceListItem, type MeResponse } from '../api/auth';
import { useFeather } from '../shell/useFeather';
import { initials, spaceColor, SpaceIcon, Avatar } from './ukoly-shared';
import { UkolyDetail } from './UkolyDetail';
import { UkolyCreate } from './UkolyCreate';
import './ukoly.css';
import { promptDialog } from '../shell/dialogs';
import { Button } from '../components/arc/button/button';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { ArcPage, ArcPageHeader, ArcPanel, ArcTabs } from '../components/arc/primitives/arc-primitives';
import './ukoly-arc.css';

const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: 'Nové',
  in_progress: 'Rozpracované',
  waiting: 'Čekající',
  blocked: 'Blokované',
  review: 'Revize',
  active: 'Aktivní',
  done: 'Hotové',
  cancelled: 'Zrušeno',
};

// Pořadí statusů ve filtru = 1:1 s ERP (Nové, Čekající, Rozpracované, Revize,
// Aktivní, Hotové, Zrušeno) - 'blocked' ERP ve filtru nemá.
const STATUS_FILTER_ORDER: TaskStatus[] = ['todo', 'waiting', 'in_progress', 'review', 'active', 'done', 'cancelled'];

const PRIORITY_FILTER_ORDER: TaskPriority[] = ['low', 'normal', 'high', 'urgent'];

const PRIORITY_FILTER_LABEL: Record<TaskPriority, string> = { low: 'Low', normal: 'Medium', high: 'High', urgent: 'Urgent' };

const PRIORITY: Record<TaskPriority, { label: string; cls: string }> = {
  low: { label: 'low', cls: 'prio-low' },
  normal: { label: 'medium', cls: 'prio-medium' },
  high: { label: 'high', cls: 'prio-high' },
  urgent: { label: 'urgent', cls: 'prio-urgent' },
};

const NOTIF_ACTION: Record<string, string> = {
  assigned: 'ti přiřadil(a) úkol',
  mentioned: 'tě označil(a) v úkolu',
  status_changed: 'změnil(a) stav úkolu',
  completed: 'dokončil(a) úkol',
  commented: 'okomentoval(a) úkol',
  reacted: 'zareagoval(a) na úkol',
};
const NOTIF_TYPE_LABEL: Record<string, string> = {
  assigned: 'Přiřazeno',
  mentioned: 'Označen',
  status_changed: 'Změna statusu',
  completed: 'Dokončeno',
  commented: 'Komentář',
  reacted: 'Reakce',
};

function dueLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}.`;
}

function relativeAge(iso: string): string {
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'dnes';
  if (days === 1) return 'včera';
  if (days < 7) return `před ${days} dny`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `před ${w} ${w === 1 ? 'týdnem' : 'týdny'}`;
  }
  const m = Math.floor(days / 30);
  return `před ${m} měs.`;
}

function isOverdue(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

interface Group {
  key: string;
  label: string;
  color: string;
  items: TaskListItem[];
}

interface UkolyPageProps {
  selectedTaskId: number | null;
  onSelectTask: (id: number | null) => void;
}

export function UkolyPage({ selectedTaskId, onSelectTask }: UkolyPageProps) {
  const selected = selectedTaskId;
  const setSelected = onSelectTask;
  const [tasks, setTasks] = useState<TaskListItem[] | null>(null);
  const [spaces, setSpaces] = useState<SpaceListItem[]>([]);
  const [projects, setProjects] = useState<TaskProject[]>([]);
  const [users, setUsers] = useState<TaskUser[]>([]);
  const [activeProject, setActiveProject] = useState<number | null>(null);
  const [view, setView] = useState<'mine' | 'delegated' | 'aiteam' | 'inbox' | 'search'>('mine');
  const [inboxScope, setInboxScope] = useState<'active' | 'mentions' | 'archive' | 'all'>('active');
  const [notifications, setNotifications] = useState<TaskNotification[] | null>(null);
  const [notifCount, setNotifCount] = useState(0);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const [adding, setAdding] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [spaceMenuOpen, setSpaceMenuOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all');
  const [filterPriorities, setFilterPriorities] = useState<Set<TaskPriority>>(() => new Set());
  const [filterSpace, setFilterSpace] = useState<number | 'all'>('all');
  const [filterUser, setFilterUser] = useState<number | 'mine'>('mine');
  const [showDone, setShowDone] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIncludeDone, setSearchIncludeDone] = useState(false);
  const [searchResults, setSearchResults] = useState<TaskListItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [view, activeProject]);

  useEffect(() => {
    const open = () => setSidebarOpen(true);
    window.addEventListener('p1:open-module-nav', open);
    return () => window.removeEventListener('p1:open-module-nav', open);
  }, []);
  const filterCount =
    (filterStatus !== 'all' ? 1 : 0) +
    (filterPriorities.size > 0 ? 1 : 0) +
    (filterSpace !== 'all' ? 1 : 0) +
    (filterUser !== 'mine' ? 1 : 0) +
    (showDone ? 1 : 0);
  // Creating a category (project) is admin-only (Dan 2026-05-31).
  const isTasksAdmin = !!me?.permissionKeys?.includes('tasks:admin');

  async function addCategory() {
    const targetSpaceId = filterSpace !== 'all' ? filterSpace : (me?.activeSpaceId ?? spaces[0]?.id ?? null);
    if (targetSpaceId == null) { setError('Vyber nejdřív Space.'); return; }
    const name = (await promptDialog({ title: 'Nová kategorie', label: 'Název kategorie', confirmLabel: 'Vytvořit' }))?.trim();
    if (!name) return;
    try {
      await createProject(targetSpaceId, name);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(String(e));
    }
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listTasks(),
      listSpaces().catch(() => []),
      getMe().catch(() => null),
      listProjects().catch(() => []),
      listTaskUsers().catch(() => []),
    ])
      .then(([t, s, m, p, u]) => {
        if (cancelled) return;
        setTasks(t);
        setSpaces(s);
        setMe(m);
        setProjects(p);
        setUsers(u);
      })
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    countNotifications().then((r) => setNotifCount(r.count)).catch(() => {});
  }, [reloadKey]);

  // Načti notifikace při otevření Inboxu. NEoznačujeme automaticky jako
  // přečtené - 1:1 s ERP: nepřečtené zůstanou zvýrazněné, dokud uživatel
  // neklikne na položku nebo na „Označit vše za přečtené".
  useEffect(() => {
    if (view !== 'inbox') return;
    let cancelled = false;
    listNotifications().then((n) => { if (!cancelled) setNotifications(n); }).catch(() => {});
    return () => { cancelled = true; };
  }, [view]);

  async function markAllRead() {
    await markAllNotificationsSeen().catch(() => {});
    const now = new Date().toISOString();
    setNotifications((cur) => (cur ? cur.map((n) => (n.seenAt == null ? { ...n, seenAt: now } : n)) : cur));
    setNotifCount(0);
  }

  function openNotif(n: TaskNotification) {
    setSelected(n.taskId);
    if (n.seenAt == null) {
      markNotificationSeen(n.id).catch(() => {});
      setNotifications((cur) => (cur ? cur.map((x) => (x.id === n.id ? { ...x, seenAt: new Date().toISOString() } : x)) : cur));
      setNotifCount((c) => Math.max(0, c - 1));
    }
  }

  async function toggleTaskDone(t: TaskListItem, e: React.MouseEvent) {
    e.stopPropagation();
    const next: TaskStatus = t.status === 'done' ? 'todo' : 'done';
    const apply = (st: TaskStatus) => {
      setTasks((cur) => (cur ? cur.map((x) => (x.id === t.id ? { ...x, status: st } : x)) : cur));
      setSearchResults((cur) => (cur ? cur.map((x) => (x.id === t.id ? { ...x, status: st } : x)) : cur));
    };
    apply(next);
    try {
      await updateTask(t.id, { status: next });
      if (next === 'done' && t.recurrence && t.recurrence !== 'none') {
        setReloadKey((k) => k + 1);
      }
    } catch {
      apply(t.status); // revert
    }
  }

  function markSeenRow(n: TaskNotification) {
    if (n.seenAt != null) return;
    markNotificationSeen(n.id).catch(() => {});
    setNotifications((cur) => (cur ? cur.map((x) => (x.id === n.id ? { ...x, seenAt: new Date().toISOString() } : x)) : cur));
    setNotifCount((c) => Math.max(0, c - 1));
  }

  useEffect(() => {
    if (view !== 'search') return;
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(() => {
      searchTasks(q, searchIncludeDone)
        .then((r) => { if (!cancelled) setSearchResults(r); })
        .catch(() => { if (!cancelled) setSearchResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [view, searchQuery, searchIncludeDone]);

  const spaceById = useMemo(() => {
    const map = new Map<number, SpaceListItem>();
    for (const s of spaces) map.set(s.id, s);
    return map;
  }, [spaces]);

  const projectById = useMemo(() => {
    const map = new Map<number, TaskProject>();
    for (const p of projects) map.set(p.id, p);
    return map;
  }, [projects]);

  const userName = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of users) {
      map.set(u.id, [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || `#${u.id}`);
    }
    return map;
  }, [users]);
  const avatarById = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const u of users) map.set(u.id, u.avatarUrl);
    return map;
  }, [users]);

  // Základní rozsah (view + uživatel + space + kategorie) BEZ status/showDone -
  // slouží jako jmenovatel „X z Y úkolů" (1:1 ERP: Y = úkoly v rozsahu, vč. hotových).
  const scopedTasks = useMemo(() => {
    if (!tasks) return [];
    return tasks.filter((t) => {
      if (activeProject != null) {
        if (t.projectId !== activeProject) return false;
      } else if (view === 'mine') {
        const wantUser = filterUser === 'mine' ? me?.userId : filterUser;
        if (!(wantUser != null && t.assigneeUserId === wantUser)) return false;
      } else if (view === 'delegated') {
        if (!(me && t.createdByUserId === me.userId && t.assigneeUserId != null && t.assigneeUserId !== me.userId)) return false;
        if (filterUser !== 'mine' && t.assigneeUserId !== filterUser) return false;
      } else if (view === 'aiteam' || view === 'inbox' || view === 'search') {
        return false; // placeholder views - rendered separately
      }
      if (filterSpace !== 'all' && t.primarySpaceId !== filterSpace) return false;
      return true;
    });
  }, [tasks, activeProject, view, me, filterSpace, filterUser]);

  const groups: Group[] = useMemo(() => {
    if (!tasks) return [];
    const open = scopedTasks.filter((t) => {
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      if (filterPriorities.size > 0 && !filterPriorities.has(t.priority)) return false;
      if (!showDone && (t.status === 'done' || t.status === 'cancelled')) return false;
      return true;
    });
    const byDueAsc = (a: typeof open[number], b: typeof open[number]) =>
      (a.dueAt ?? '').localeCompare(b.dueAt ?? '');
    const byCreatedDesc = (a: typeof open[number], b: typeof open[number]) =>
      (b.createdAt ?? '').localeCompare(a.createdAt ?? '') || b.id - a.id;
    const overdue = open.filter((t) => isOverdue(t.dueAt)).sort(byDueAsc);
    const dated = open.filter((t) => t.dueAt && !isOverdue(t.dueAt)).sort(byDueAsc);
    const noDue = open.filter((t) => !t.dueAt).sort(byCreatedDesc);
    const g: Group[] = [];
    if (overdue.length) g.push({ key: 'overdue', label: 'PO TERMÍNU', color: '#c82b23', items: overdue });
    if (dated.length) g.push({ key: 'dated', label: 'NADCHÁZEJÍCÍ', color: '#0071e3', items: dated });
    if (noDue.length) g.push({ key: 'nodue', label: 'BEZ TERMÍNU', color: '#8e8e93', items: noDue });
    return g;
  }, [tasks, scopedTasks, filterStatus, filterPriorities, showDone]);

  const openCount = groups.reduce((n, g) => n + g.items.length, 0);
  const total = scopedTasks.length;
  const pageTitle = activeProject != null
    ? projectById.get(activeProject)?.name ?? 'Kategorie'
    : view === 'delegated' ? 'Delegované'
      : view === 'aiteam' ? 'AI Team'
        : view === 'inbox' ? 'Inbox'
          : view === 'search' ? 'Hledat'
            : 'Moje úkoly';
  useFeather([groups.length, spaces.length]);

  return (
    <>
    <div className="asn-wrap asn-arc-page p1-arc-scope">
      <div className={`asn-sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <aside className={`asn-sidebar${sidebarOpen ? ' open' : ''}`}>
        <a className={`asn-nav-item${view === 'mine' && activeProject == null ? ' active' : ''}`} onClick={() => { setView('mine'); setActiveProject(null); }}>
          <span className="feather-icon"><i data-feather="check-square"></i></span>
          <span>Moje úkoly</span>
        </a>
        <a className={`asn-nav-item${view === 'search' ? ' active' : ''}`} onClick={() => { setView('search'); setActiveProject(null); }}>
          <span className="feather-icon"><i data-feather="search"></i></span>
          <span>Hledat</span>
        </a>
        <a className={`asn-nav-item${view === 'delegated' && activeProject == null ? ' active' : ''}`} onClick={() => { setView('delegated'); setActiveProject(null); }}>
          <span className="feather-icon"><i data-feather="user-check"></i></span>
          <span>Delegované</span>
        </a>
        <a className={`asn-nav-item${view === 'aiteam' ? ' active' : ''}`} onClick={() => { setView('aiteam'); setActiveProject(null); }}>
          <span className="feather-icon"><i data-feather="cpu"></i></span>
          <span>AI Team</span>
        </a>
        <a className={`asn-nav-item${view === 'inbox' ? ' active' : ''}`} onClick={() => { setView('inbox'); setActiveProject(null); }}>
          <span className="feather-icon"><i data-feather="inbox"></i></span>
          <span>Inbox</span>
          {notifCount > 0 && <span className="asn-badge">{notifCount}</span>}
        </a>

        <div className="asn-space-switcher">
          <button
            type="button"
            className="asn-space-switcher-btn"
            aria-label="Vybrat Space"
            onClick={() => setSpaceMenuOpen((v) => !v)}
          >
            <SpaceIcon space={filterSpace === 'all' ? undefined : spaceById.get(filterSpace)} />
            <span className="asn-space-switcher-name">
              {filterSpace === 'all' ? 'Všechny Spaces' : spaceById.get(filterSpace)?.name ?? 'Vyber Space'}
            </span>
            <span className="asn-space-switcher-chevron feather-icon"><i data-feather="chevron-down"></i></span>
          </button>
          {spaceMenuOpen && (
            <div className="asn-space-switcher-menu open">
              <a
                className={`asn-space-menu-item${filterSpace === 'all' ? ' active' : ''}`}
                onClick={() => { setFilterSpace('all'); setActiveProject(null); setSpaceMenuOpen(false); }}
              >
                <SpaceIcon space={undefined} />
                <span>Všechny Spaces</span>
              </a>
              {spaces.map((s) => (
                <a
                  key={s.id}
                  className={`asn-space-menu-item${filterSpace === s.id ? ' active' : ''}`}
                  onClick={() => { setFilterSpace(s.id); setActiveProject(null); setSpaceMenuOpen(false); }}
                >
                  <SpaceIcon space={s} />
                  <span>{s.name}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="asn-section-title asn-section-title-row">
          <span>Kategorie</span>
          {isTasksAdmin && (
            <button type="button" className="asn-cat-add" title="Nová kategorie" onClick={() => void addCategory()}>
              <span className="feather-icon"><i data-feather="plus"></i></span>
            </button>
          )}
        </div>
        {(() => {
          const renderItem = (p: TaskProject) => (
            <a
              key={p.id}
              className={`asn-project${activeProject === p.id ? ' active' : ''}`}
              onClick={() => { setView('mine'); setActiveProject((cur) => (cur === p.id ? null : p.id)); }}
            >
              <span className="asn-project-dot" style={{ background: spaceColor(p.id + 2) }}></span>
              <span className="asn-project-name">{p.name}</span>
            </a>
          );
          if (filterSpace !== 'all') {
            return projects.filter((p) => p.spaceId === filterSpace).map(renderItem);
          }
          const knownSpaceIds = new Set(spaces.map((s) => s.id));
          const orphans = projects.filter((p) => !knownSpaceIds.has(p.spaceId));
          return (
            <>
              {spaces
                .filter((s) => projects.some((p) => p.spaceId === s.id))
                .map((s) => (
                  <div key={s.id} className="asn-cat-space-group">
                    <div className="asn-cat-space-head">
                      <SpaceIcon space={s} size={16} />
                      <span>{s.name}</span>
                    </div>
                    {projects.filter((p) => p.spaceId === s.id).map(renderItem)}
                  </div>
                ))}
              {orphans.length > 0 && (
                <div className="asn-cat-space-group">
                  <div className="asn-cat-space-head">
                    <SpaceIcon space={undefined} size={16} />
                    <span>Ostatní</span>
                  </div>
                  {orphans.map(renderItem)}
                </div>
              )}
            </>
          );
        })()}
      </aside>

      <ArcPage as="main" className="asn-main asn-arc-main">
        <ArcPageHeader eyebrow="ÚKOLY" title={pageTitle} />

        {view !== 'search' && <div className="asn-toolbar">
          {view === 'inbox' && (
            <ArcTabs className="asn-arc-tabs" items={[
              { value: 'active', label: `Aktivita ${(notifications ?? []).filter((n) => n.seenAt == null).length}` },
              { value: 'mentions', label: `@Zmínky ${(notifications ?? []).filter((n) => n.type === 'mentioned' && n.seenAt == null).length}` },
              { value: 'archive', label: `Archiv ${(notifications ?? []).filter((n) => n.seenAt != null).length}` },
              { value: 'all', label: 'Vše' },
            ]} value={inboxScope} onChange={setInboxScope} label="Pohled na Inbox" />
          )}
          {view !== 'inbox' && (
            <span className="asn-toolbar-count">
              <strong>{openCount}</strong> z {total} úkolů
            </span>
          )}
          {view !== 'inbox' && (
          <div className="mine-filter-chip-wrap">
            <Button type="button" variant="secondary" size="sm" className={`mine-filter-chip${filterCount ? ' active' : ''}`} onClick={() => setShowFilter((v) => !v)}>
              <span className="feather-icon"><i data-feather="filter"></i></span>
              <span>Filtr</span>
              <span className="mine-filter-chip-count">{filterCount}</span>
            </Button>
            {showFilter && (
              <div className="mine-filter-popover open">
                <div className="mine-filter-popover-head">
                  <span>Filtry</span>
                  <button type="button" className="mine-filter-popover-close" aria-label="Zavřít" onClick={() => setShowFilter(false)}>
                    <span className="feather-icon"><i data-feather="x"></i></span>
                  </button>
                </div>
                <div className="mine-filter-popover-row">
                  <label>Status</label>
                  <CompactSelect className="asn-arc-select" value={filterStatus} onChange={(next) => setFilterStatus(next as TaskStatus | 'all')} ariaLabel="Status" options={[{ value: 'all', label: 'Vše' }, ...STATUS_FILTER_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]} />
                </div>
                <div className="mine-filter-popover-row">
                  <label>Priorita</label>
                  <div className="mine-filter-priority" role="group" aria-label="Priorita">
                    {PRIORITY_FILTER_ORDER.map((pr) => {
                      const checked = filterPriorities.has(pr);
                      return (
                        <label key={pr} className={`mine-filter-priority-chip${checked ? ' checked' : ''}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setFilterPriorities((cur) => {
                                const next = new Set(cur);
                                if (e.target.checked) next.add(pr); else next.delete(pr);
                                return next;
                              });
                            }}
                          />
                          <span>{PRIORITY_FILTER_LABEL[pr]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="mine-filter-popover-row">
                  <label>Space</label>
                  <CompactSelect className="asn-arc-select" value={String(filterSpace)} onChange={(next) => setFilterSpace(next === 'all' ? 'all' : Number(next))} ariaLabel="Space" options={[{ value: 'all', label: 'Všechny' }, ...spaces.map((s) => ({ value: String(s.id), label: s.name }))]} />
                </div>
                <div className="mine-filter-popover-row">
                  <label>Uživatel</label>
                  <CompactSelect className="asn-arc-select" value={String(filterUser)} onChange={(next) => setFilterUser(next === 'mine' ? 'mine' : Number(next))} ariaLabel="Uživatel" options={[{ value: 'mine', label: view === 'delegated' ? 'Všichni' : 'Moje' }, ...[...users].sort((a, b) => (userName.get(a.id) ?? '').localeCompare(userName.get(b.id) ?? '', 'cs')).map((u) => ({ value: String(u.id), label: userName.get(u.id) ?? `#${u.id}` }))]} />
                </div>
                <div className="mine-filter-popover-row">
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textTransform: 'none', fontWeight: 400 }}>
                    <input type="checkbox" checked={!showDone} onChange={(e) => setShowDone(!e.target.checked)} /> Jen nevyřešené
                  </label>
                </div>
                <div className="mine-filter-popover-actions">
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setFilterStatus('all'); setFilterPriorities(new Set()); setFilterSpace('all'); setFilterUser('mine'); setShowDone(false); }}>
                    Resetovat
                  </Button>
                  <Button type="button" variant="primary" size="sm" onClick={() => setShowFilter(false)}>
                    Použít
                  </Button>
                </div>
              </div>
            )}
          </div>
          )}
          <span className="asn-toolbar-spacer"></span>
          {view === 'inbox' ? (
            notifications && notifications.some((n) => n.seenAt == null) && (
              <Button type="button" variant="secondary" size="sm" className="asn-mark-all-read" title="Označit vše za přečtené" onClick={() => void markAllRead()}>
                <span className="feather-icon"><i data-feather="check-circle"></i></span>
                Přečíst vše
              </Button>
            )
          ) : (
            <Button type="button" variant="primary" size="sm" className="asn-add-task-btn" onClick={() => setAdding(true)}>
              <span className="feather-icon me-1"><i data-feather="plus"></i></span>
              Přidat úkol
            </Button>
          )}
        </div>}

        {view === 'inbox' ? (
          notifications == null ? (
            <div className="asn-empty">Načítám…</div>
          ) : (() => {
            const scoped = notifications.filter((n) =>
              inboxScope === 'active' ? n.seenAt == null
                : inboxScope === 'mentions' ? n.type === 'mentioned'
                  : inboxScope === 'archive' ? n.seenAt != null
                    : true);
            if (scoped.length === 0) {
              return (
                <div className="asn-empty">
                  {inboxScope === 'archive' ? 'Archiv je prázdný.'
                    : inboxScope === 'mentions' ? 'Žádné zmínky.'
                      : 'Inbox - žádné notifikace.'}
                </div>
              );
            }
            return (
              <ArcPanel as="div" className="asn-notif-list">
                {scoped.map((n) => {
                  const actor = n.actorUserId != null ? userName.get(n.actorUserId) ?? 'Někdo' : 'Systém';
                  return (
                    <div
                      key={n.id}
                      className={`asn-notif-item${n.seenAt == null ? ' unseen' : ''}`}
                      role="button"
                      onClick={() => openNotif(n)}
                    >
                      {n.seenAt == null && <span className="asn-notif-unseen-dot"></span>}
                      <span className="asn-notif-avatar">{initials(actor)}</span>
                      <div className="asn-notif-body">
                        <div className="asn-notif-head">
                          <span className="asn-notif-actor">{actor}</span>
                          <span className={`asn-notif-type notif-type-${n.type}`}>{NOTIF_TYPE_LABEL[n.type] ?? n.type}</span>
                          <span className="asn-notif-time">{relativeAge(n.createdAt)}</span>
                        </div>
                        <div className="asn-notif-text">
                          {NOTIF_ACTION[n.type] ?? 'aktivita'} <strong>{n.taskTitle ?? `#${n.taskId}`}</strong>
                        </div>
                      </div>
                      {n.seenAt == null && (
                        <button
                          type="button"
                          className="asn-notif-row-action"
                          title="Označit jako přečtené a přesunout do Archivu"
                          onClick={(e) => { e.stopPropagation(); markSeenRow(n); }}
                        >
                          <span className="feather-icon"><i data-feather="eye"></i></span>
                          Přečteno
                        </button>
                      )}
                    </div>
                  );
                })}
              </ArcPanel>
            );
          })()
        ) : view === 'search' ? (
          <div className="asn-search">
            <ArcPanel as="div" className="asn-search-bar">
              <input
                type="text"
                className="asn-search-input"
                aria-label="Hledat v úkolech"
                placeholder="Hledat v názvu nebo popisu…"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <label className="asn-search-done-label">
                <input type="checkbox" checked={searchIncludeDone} onChange={(e) => setSearchIncludeDone(e.target.checked)} /> Včetně hotových
              </label>
              {searchQuery.trim().length >= 2 && <span className="asn-search-count">{searchResults?.length ?? 0} výsledků</span>}
            </ArcPanel>
            {searchQuery.trim().length < 2 ? (
              <div className="asn-empty">Zadej alespoň 2 znaky…</div>
            ) : searching && searchResults == null ? (
              <div className="asn-empty">Hledám…</div>
            ) : !searchResults || searchResults.length === 0 ? (
              <div className="asn-empty">Žádné výsledky.</div>
            ) : (
              <ArcPanel as="div" className="asn-task-list">
                {searchResults.map((t) => {
                  const sp = spaceById.get(t.primarySpaceId);
                  const assigneeName = t.assigneeUserId != null ? userName.get(t.assigneeUserId) ?? '-' : '';
                  const prio = PRIORITY[t.priority];
                  return (
                    <div key={t.id} className={`asn-task-row${selected === t.id ? ' active' : ''}`} role="button" onClick={() => setSelected(t.id)}>
                      <span
                        className={`asn-task-check${t.status === 'done' ? ' checked' : ''}`}
                        role="button"
                        title={t.status === 'done' ? 'Znovu otevřít' : 'Označit jako hotové'}
                        onClick={(e) => void toggleTaskDone(t, e)}
                      ></span>
                      <span className={`asn-task-assignee${assigneeName ? '' : ' empty'}`}>
                        <Avatar name={assigneeName || '-'} userId={t.assigneeUserId} avatarUrl={t.assigneeUserId != null ? avatarById.get(t.assigneeUserId) : null} size={22} />
                        <span>{assigneeName || 'Nepřiřazeno'}</span>
                      </span>
                      <span className="asn-task-title">{t.title}</span>
                      {sp && (
                        <span className="asn-task-project">
                          <span className="asn-task-space-icon" style={{ background: spaceColor(sp.id) }}>{sp.name.charAt(0).toUpperCase()}</span>
                          <strong>{t.projectId != null ? projectById.get(t.projectId)?.name ?? sp.name : sp.name}</strong>
                        </span>
                      )}
                      <span className={`asn-status-pill status-${t.status}`}>{STATUS_LABEL[t.status]}</span>
                      {t.priority !== 'normal' && <span className={`task-badge ${prio.cls}`}>{prio.label}</span>}
                      {t.dueAt && <span className={`asn-task-due${isOverdue(t.dueAt) ? ' overdue' : ''}`}>{dueLabel(t.dueAt)}</span>}
                    </div>
                  );
                })}
              </ArcPanel>
            )}
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : !tasks ? (
          <div className="asn-empty">Načítám…</div>
        ) : openCount === 0 ? (
          <div className="asn-empty">
            {view === 'aiteam'
              ? 'AI Team - zatím žádné úkoly přiřazené AI agentům.'
              : view === 'delegated'
                ? 'Žádné delegované úkoly.'
                : 'Žádné úkoly.'}
          </div>
        ) : (
          groups.map((g) => (
            <ArcPanel key={g.key} as="section" className={`asn-section${collapsed[g.key] ? ' collapsed' : ''}`}>
              <div
                className="asn-section-header"
                onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
              >
                <span className="asn-section-chevron">▾</span>
                <span className="asn-section-color" style={{ background: g.color }}></span>
                <span className="asn-section-title">{g.label}</span>
                <span className="asn-section-count">{g.items.length}</span>
              </div>
              <div className="asn-section-body">
                <ArcPanel as="div" className="asn-task-list">
                  {g.items.map((t) => {
                    const sp = spaceById.get(t.primarySpaceId);
                    const assigneeName = t.assigneeUserId != null ? userName.get(t.assigneeUserId) ?? '-' : '';
                    const prio = PRIORITY[t.priority];
                    return (
                      <div key={t.id} className={`asn-task-row${selected === t.id ? ' active' : ''}`} role="button" onClick={() => setSelected(t.id)}>
                        <span
                        className={`asn-task-check${t.status === 'done' ? ' checked' : ''}`}
                        role="button"
                        title={t.status === 'done' ? 'Znovu otevřít' : 'Označit jako hotové'}
                        onClick={(e) => void toggleTaskDone(t, e)}
                      ></span>
                        <span className={`asn-task-assignee${assigneeName ? '' : ' empty'}`}>
                          <Avatar name={assigneeName || '-'} userId={t.assigneeUserId} avatarUrl={t.assigneeUserId != null ? avatarById.get(t.assigneeUserId) : null} size={22} />
                          <span>{assigneeName || 'Nepřiřazeno'}</span>
                        </span>
                        <span className="asn-task-title">{t.title}</span>
                        {t.subtaskCount ? (
                          <span className="asn-subtask-count">↳ {t.subtaskCount}</span>
                        ) : null}
                        {sp && (
                          <span className="asn-task-project">
                            <span className="asn-task-space-icon" style={{ background: spaceColor(sp.id) }}>
                              {sp.name.charAt(0).toUpperCase()}
                            </span>
                            <strong>{t.projectId != null ? projectById.get(t.projectId)?.name ?? sp.name : sp.name}</strong>
                          </span>
                        )}
                        <span className={`asn-status-pill status-${t.status}`}>{STATUS_LABEL[t.status]}</span>
                        {t.priority !== 'normal' && (
                          <span className={`task-badge ${prio.cls}`}>{prio.label}</span>
                        )}
                        {t.dueAt && (
                          <span className={`asn-task-due${isOverdue(t.dueAt) ? ' overdue' : ''}`}>
                            {dueLabel(t.dueAt)}
                          </span>
                        )}
                        <span className="asn-task-age">{relativeAge(t.createdAt ?? new Date().toISOString())}</span>
                      </div>
                    );
                  })}
                </ArcPanel>
              </div>
            </ArcPanel>
          ))
        )}
      </ArcPage>

      {view !== 'inbox' && <Button type="button" variant="primary" size="sm" className="asn-fab" aria-label="Přidat úkol" onClick={() => setAdding(true)}>
        +
      </Button>}
    </div>
    {adding && (
      <UkolyCreate
        spaces={spaces}
        projects={projects}
        users={users}
        defaultSpaceId={
          // Když je otevřená konkrétní kategorie, předvyplň její Space (ne default).
          activeProject != null
            ? projectById.get(activeProject)?.spaceId ?? null
            : filterSpace !== 'all'
              ? filterSpace
              : me?.activeSpaceId ?? spaces[0]?.id ?? null
        }
        defaultProjectId={activeProject}
        onClose={() => setAdding(false)}
        onCreated={(taskId) => { setAdding(false); setReloadKey((k) => k + 1); setSelected(taskId); }}
      />
    )}
    {selected != null && (
      <UkolyDetail taskId={selected} spaces={spaces} projects={projects} users={users} me={me} onClose={() => setSelected(null)} onChanged={() => setReloadKey((k) => k + 1)} onOpenTask={(id) => setSelected(id)} />
    )}
    </>
  );
}
